import { useCallback, useState } from "react";
import Stack from "@mui/material/Stack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { setMessage, useMaterialUIController } from "context";
import MDButton from "components/MDButton";
import { LojaPageContainer } from "../../common";
import FinanceiroFiltroPeriodo, {
  type PeriodoRapido,
} from "../dashboard/components/FinanceiroFiltroPeriodo";
import type { DashboardFinanceiroFiltros } from "../dashboard/types/financeiro-dashboard.types";
import { useDREGerencial } from "../dashboard/hooks/useDREGerencial";
import DREGerencialSection from "../dashboard/components/DREGerencialSection";
import {
  exportDreGerencialExcel,
  exportDreGerencialPdf,
} from "../dashboard/helpers/export-dre-gerencial.helpers";

function filtrosMesAtual(): DashboardFinanceiroFiltros {
  const now = new Date();
  return {
    dataInicio: format(startOfMonth(now), "yyyy-MM-dd"),
    dataFim: format(endOfMonth(now), "yyyy-MM-dd"),
    granularidade: "mensal",
  };
}

/**
 * DRE gerencial global — GET /loja/financeiro/dre-gerencial
 */
export default function LojaFinanceiroDREGerencialPage() {
  const [, dispatch] = useMaterialUIController();
  const [filtros, setFiltros] = useState<DashboardFinanceiroFiltros>(() =>
    filtrosMesAtual(),
  );
  const [filtrosAplicados, setFiltrosAplicados] =
    useState<DashboardFinanceiroFiltros>(() => filtrosMesAtual());
  const [periodoRapido, setPeriodoRapido] = useState<PeriodoRapido>("mes");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const aplicarFiltros = useCallback((override?: DashboardFinanceiroFiltros) => {
    setFiltrosAplicados(override ?? filtros);
  }, [filtros]);

  const dre = useDREGerencial(filtrosAplicados);

  const exportDisabled =
    dre.loading ||
    Boolean(dre.error) ||
    dre.isEmpty ||
    !dre.data ||
    exportingPdf ||
    exportingExcel;

  const periodoExport = {
    dataInicio: filtrosAplicados.dataInicio,
    dataFim: filtrosAplicados.dataFim,
    granularidade: filtrosAplicados.granularidade,
  };

  const handleExportPdf = useCallback(async () => {
    if (!dre.data) return;
    setExportingPdf(true);
    try {
      await exportDreGerencialPdf({ view: dre.data, periodo: periodoExport });
    } catch (err) {
      const text =
        err instanceof Error ? err.message : "Não foi possível gerar o PDF.";
      setMessage(dispatch, { open: true, severity: "error", text });
    } finally {
      setExportingPdf(false);
    }
  }, [dre.data, periodoExport, dispatch]);

  const handleExportExcel = useCallback(() => {
    if (!dre.data) return;
    setExportingExcel(true);
    try {
      exportDreGerencialExcel({ view: dre.data, periodo: periodoExport });
    } catch (err) {
      const text =
        err instanceof Error ? err.message : "Não foi possível gerar o Excel.";
      setMessage(dispatch, { open: true, severity: "error", text });
    } finally {
      setExportingExcel(false);
    }
  }, [dre.data, periodoExport, dispatch]);

  return (
    <LojaPageContainer
      title="DRE gerencial"
      subtitle="Demonstrativo de resultado por competência no período selecionado."
      actions={
        <Stack direction="row" spacing={1}>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            disabled={exportDisabled}
            onClick={() => void handleExportPdf()}
          >
            {exportingPdf ? "Gerando…" : "Exportar PDF"}
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            startIcon={<GridOnIcon />}
            disabled={exportDisabled}
            onClick={handleExportExcel}
          >
            {exportingExcel ? "Gerando…" : "Exportar Excel"}
          </MDButton>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <FinanceiroFiltroPeriodo
          filtros={filtros}
          onChange={setFiltros}
          onApply={aplicarFiltros}
          periodoRapido={periodoRapido}
          onPeriodoRapidoChange={setPeriodoRapido}
        />

        <DREGerencialSection
          embedded
          data={dre.data}
          loading={dre.loading}
          error={dre.error}
          isEmpty={dre.isEmpty}
          refetch={dre.refetch}
        />
      </Stack>
    </LojaPageContainer>
  );
}
